// ============================================================
//  ADMINS PAGE — ADMIN DASHBOARD
//  Path: src/pages/AdminsPage.tsx
//  Super admin only — includes password reset
//
//  FIX: every action here (create admin, change role, remove admin,
//  reset password) is behind requireRecentAuth(5, ...) on the backend —
//  signed in within the last 5 minutes. The page never re-verified the
//  admin, so after 5 minutes every action failed, and role change /
//  remove failed with no message at all (console only). Each action now
//  goes through useRecentAuth (asks for password + authenticator code
//  first when needed), and role change / remove show their errors.
// ============================================================

import React, { useEffect, useState } from 'react'
import { listAdmins, createAdmin, updateAdminRole, removeAdmin, resetUserPassword } from '../services/admin.service'
import { useRecentAuth } from '../hooks/useRecentAuth'

interface AdminUser {
  id: string; firstName: string; lastName: string; email: string | null
  roles: string[]; isActive: boolean; createdAt: string
}

const AdminsPage: React.FC = () => {
  const [admins, setAdmins]           = useState<AdminUser[]>([])
  const [loading, setLoading]         = useState(true)
  const [showCreate, setShowCreate]   = useState(false)
  const [form, setForm]               = useState({ email: '', password: '', firstName: '', lastName: '', role: 'admin' })
  const [createError, setCreateError] = useState('')
  const [creating, setCreating]       = useState(false)

  // Password reset state
  const [resetModal, setResetModal]       = useState<AdminUser | null>(null)
  const [resetPasswordVal, setResetPasswordVal] = useState('')
  const [resetError, setResetError]       = useState('')
  const [resetSuccess, setResetSuccess]   = useState('')
  const [resetting, setResetting]         = useState(false)

  // Role change / remove errors — previously console-only.
  const [actionError, setActionError]     = useState('')
  const { runWithRecentAuth, reauthModal } = useRecentAuth()

  const fetchAdmins = async () => {
    setLoading(true)
    try {
      const res = await listAdmins()
      setAdmins(res.data.data)
    } catch (err) {
      console.error('Failed to fetch admins:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAdmins() }, [])

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setCreateError('')
    runWithRecentAuth(async () => {
      setCreating(true)
      try {
        await createAdmin(form)
        setShowCreate(false)
        setForm({ email: '', password: '', firstName: '', lastName: '', role: 'admin' })
        fetchAdmins()
      } catch (err: any) {
        setCreateError(err.response?.data?.message || err.response?.data?.errors?.join(', ') || 'Failed to create admin')
      } finally {
        setCreating(false)
      }
    })
  }

  const handleRoleChange = (id: string, role: string) => {
    setActionError('')
    runWithRecentAuth(async () => {
      try {
        await updateAdminRole(id, role)
        fetchAdmins()
      } catch (err: any) {
        console.error('Failed to update role:', err)
        setActionError(err?.response?.data?.message || 'Failed to change role — try again.')
      }
    })
  }

  const handleRemove = (id: string, name: string) => {
    if (!window.confirm(`Remove admin access for ${name}?`)) return
    setActionError('')
    runWithRecentAuth(async () => {
      try {
        await removeAdmin(id)
        fetchAdmins()
      } catch (err: any) {
        console.error('Failed to remove admin:', err)
        setActionError(err?.response?.data?.message || `Failed to remove ${name} — try again.`)
      }
    })
  }

  const handleResetPassword = async () => {
    if (!resetModal) return
    setResetError('')
    setResetSuccess('')

    if (resetPasswordVal.length < 8) {
      setResetError('Password must be at least 8 characters')
      return
    }

    const target = resetModal
    runWithRecentAuth(async () => {
      setResetting(true)
      try {
        await resetUserPassword(target.id, resetPasswordVal)
        setResetSuccess('Password reset successfully')
        setResetPasswordVal('')
        setTimeout(() => { setResetModal(null); setResetSuccess('') }, 1500)
      } catch (err: any) {
        setResetError(err.response?.data?.message || 'Failed to reset password')
      } finally {
        setResetting(false)
      }
    })
  }

  const getAdminRole = (roles: string[]) => {
    return roles.find(r => ['super_admin', 'admin', 'customer_service'].includes(r)) || '—'
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Team</h1>
          <p className="text-gray-400 text-sm mt-1">Manage administrators and their roles</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors">
          + Add Admin
        </button>
      </div>

      {actionError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {actionError}
          <button onClick={() => setActionError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}

      {/* Admin List */}
      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-light">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Name</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Email</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Role</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Joined</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">No admins found</td></tr>
            ) : (
              admins.map((a) => (
                <tr key={a.id} className="border-b border-border-light hover:bg-surface-tertiary/50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-white">{a.firstName} {a.lastName}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-300">{a.email || '—'}</td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      getAdminRole(a.roles) === 'super_admin' ? 'bg-purple-600/20 text-purple-400' :
                      getAdminRole(a.roles) === 'admin' ? 'bg-brand-600/20 text-brand-400' :
                      'bg-teal-600/20 text-teal-400'
                    }`}>{getAdminRole(a.roles).replace('_', ' ')}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">{new Date(a.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    {getAdminRole(a.roles) !== 'super_admin' ? (
                      <div className="flex gap-2">
                        <select value={getAdminRole(a.roles)} onChange={(e) => handleRoleChange(a.id, e.target.value)}
                          className="bg-surface-tertiary border border-border-light rounded-lg px-2 py-1 text-xs text-white focus:outline-none">
                          <option value="admin">Admin</option>
                          <option value="customer_service">Customer Service</option>
                        </select>
                        <button onClick={() => { setResetModal(a); setResetPasswordVal(''); setResetError(''); setResetSuccess('') }}
                          className="px-3 py-1 text-xs bg-yellow-500/10 text-yellow-400 rounded-lg hover:bg-yellow-500/20 transition-colors">
                          Reset PW
                        </button>
                        <button onClick={() => handleRemove(a.id, `${a.firstName} ${a.lastName}`)}
                          className="px-3 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-600">Protected</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Admin Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">Add New Admin</h3>
            {createError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">{createError}</p>
              </div>
            )}
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input type="text" placeholder="First Name" required value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
                <input type="text" placeholder="Last Name" required value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none" />
              </div>
              <input type="email" placeholder="Email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-3" />
              <input type="password" placeholder="Password (min 8 chars)" required minLength={8} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-3" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none mb-4">
                <option value="admin">Admin</option>
                <option value="customer_service">Customer Service</option>
              </select>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => { setShowCreate(false); setCreateError('') }}
                  className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
                <button type="submit" disabled={creating}
                  className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
                  {creating ? 'Creating...' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">Reset Password</h3>
            <p className="text-sm text-gray-400 mb-4">
              Set a new password for {resetModal.firstName} {resetModal.lastName}
            </p>

            {resetError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">{resetError}</p>
              </div>
            )}
            {resetSuccess && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                <p className="text-green-400 text-sm">{resetSuccess}</p>
              </div>
            )}

            <input type="password" placeholder="New password (min 8 characters)" value={resetPasswordVal}
              onChange={(e) => setResetPasswordVal(e.target.value)} minLength={8}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4" />

            <div className="flex gap-3 justify-end">
              <button onClick={() => setResetModal(null)}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={handleResetPassword} disabled={resetting}
                className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-xl hover:bg-yellow-500 disabled:opacity-50 transition-colors">
                {resetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reauthModal}
    </div>
  )
}

export default AdminsPage